import { MCP_TOOL_GROUPS } from "./mcp.ts";
import { modelProvidersSchema, modelProvidersWiden } from "./model-providers.ts";
import { nativeClientsPolicySchema } from "./oauth.ts";
import {
  type EgressPolicy,
  egressPolicySchema,
  webDomainsNarrow,
  webDomainsSchema,
} from "./tools.ts";
import { WIKI_TRUST_STRICTNESS, type WikiTrustLevel } from "./wiki-threads.ts";

/**
 * Settings whose change in one direction widens what people, clients or the assistant may do
 * with data (openspec change settings-typed-controls, design D5, decision R4): the backoffice
 * and the console ask before saving such a change. Product preferences are not listed. A
 * project's narrow-only keys use the registry's own rule (any move toward the inherited value).
 */
type Rule =
  /** On is wider. */
  | { kind: "on" }
  /** A lower rank is wider. */
  | { kind: "rank"; rank: Readonly<Record<string, number>> }
  /** A larger number is wider; null (no limit) is the widest. */
  | { kind: "limit" }
  /** Domain lists: wider when the new lists do not narrow the old ones. */
  | { kind: "domains" }
  /** A list of allowed items: wider when it gains one. */
  | { kind: "list" }
  /** `tools.egress`: wider when any audience moves toward Allowed. */
  | { kind: "egress" }
  /**
   * `auth.nativeClients` (#346): wider when the mode moves from the allowlist to any. Adding a
   * client to the allowlist does not ask: its editor fetches and shows its name first.
   */
  | { kind: "nativeClients" }
  /** `models.providers` (model-picking D1): wider when any kind turns on for an owner. */
  | { kind: "providers" }
  /**
   * `models.labProvider` (model-picking D5): wider when a lab's provider or one of its choices
   * is not among the previous choices (a lab left out offers ESI only).
   */
  | { kind: "labProvider" };

const ON: Rule = { kind: "on" };

const LOOSENING_RULES: Readonly<Record<string, Rule>> = {
  "tools.webSearch": ON,
  "tools.webFetch": ON,
  "tools.codeExecution": ON,
  "mcp.enabled": ON,
  "mcp.writeTools": ON,
  "wiki.mcpSelfPublish": ON,
  ...Object.fromEntries(MCP_TOOL_GROUPS.map((g) => [`mcp.tools.${g}`, ON])),
  "knowledge.declassification": ON,
  "auth.deviceGrant": ON,
  "projects.shareOutsideEntity": ON,
  "auth.mfa": { kind: "rank", rank: { optional: 0, required: 1 } },
  "models.residency": { kind: "rank", rank: { global: 0, eu: 1 } },
  "wiki.trust": {
    kind: "rank",
    rank: WIKI_TRUST_STRICTNESS satisfies Record<WikiTrustLevel, number>,
  },
  "tools.webFetch.approval": { kind: "rank", rank: { provenance: 0, always: 1 } },
  // A higher ceiling lets MCP clients read more sensitive levels.
  "mcp.classificationCeiling": { kind: "rank", rank: { named: 0, entity: 1, project: 2 } },
  "tools.webSearch.maxAgeDays": { kind: "limit" },
  "tools.webSearch.domains": { kind: "domains" },
  "tools.webFetch.domains": { kind: "domains" },
  "tools.egress": { kind: "egress" },
  "tools.codeExecution.egress": { kind: "egress" },
  "auth.nativeClients": { kind: "nativeClients" },
  "models.providers": { kind: "providers" },
  // Model-picking D1, D17 (slice 4c): an operator's unblock or a tenant's opt-in offers a lab.
  "models.labs": { kind: "list" },
};

/**
 * A project's narrow-only keys beyond the backoffice list (settings-typed-controls D5, R4): on a
 * project, any move back toward the inherited value asks, so every narrow-only key has a rule.
 */
const PROJECT_RULES: Readonly<Record<string, Rule>> = {
  "projects.tasks": ON,
  "conversations.attachments": ON,
  "conversations.artefacts": ON,
  "conversations.thinkingSummary": ON,
  "knowledge.generatedSources": ON,
  "memory.personal": ON,
  "memory.project": ON,
  "wiki.suggestions": ON,
  "models.advancedPicker": ON,
  "models.allowed": { kind: "list" },
  "models.allowedModels": { kind: "list" },
  "knowledge.ocrMaxPages": { kind: "limit" },
  // Model-picking D4, D5: offering another lab, or another provider for a lab. Project rows
  // only until the Models screen edits them at the other levels (slice 5).
  "models.labs": { kind: "list" },
  "models.labProvider": { kind: "labProvider" },
};

/** The keys {@link projectSettingLoosens} has a rule for. */
export const PROJECT_LOOSENING_KEYS: readonly string[] = [
  ...new Set([...Object.keys(PROJECT_RULES), ...Object.keys(LOOSENING_RULES)]),
];

/** The keys {@link settingLoosens} may ask about. */
export const LOOSENING_KEYS: readonly string[] = Object.keys(LOOSENING_RULES);

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * True when changing `key` from `from` to `to` widens it, so the backoffice and the console ask
 * first. A value the rule cannot read counts as wider when it differs (asking is the safe side).
 * A convenience only: the server keeps enforcing locks, narrowing and bounds.
 */
export function settingLoosens(key: string, from: unknown, to: unknown): boolean {
  return loosensBy(
    Object.hasOwn(LOOSENING_RULES, key) ? LOOSENING_RULES[key] : undefined,
    from,
    to,
  );
}

/**
 * True when a project moving `key` from `from` to `to` widens it: for a narrow-only key, any move
 * back toward the inherited value (the registry's narrowing rule, checked by a core test), so the
 * project row asks first. Same caveats as {@link settingLoosens}.
 */
export function projectSettingLoosens(key: string, from: unknown, to: unknown): boolean {
  const rule = Object.hasOwn(PROJECT_RULES, key)
    ? PROJECT_RULES[key]
    : Object.hasOwn(LOOSENING_RULES, key)
      ? LOOSENING_RULES[key]
      : undefined;
  return loosensBy(rule, from, to);
}

function loosensBy(rule: Rule | undefined, from: unknown, to: unknown): boolean {
  if (!rule || same(from, to)) {
    return false;
  }
  switch (rule.kind) {
    case "on":
      return to === true;
    case "rank": {
      const a = typeof from === "string" ? rule.rank[from] : undefined;
      const b = typeof to === "string" ? rule.rank[to] : undefined;
      return a === undefined || b === undefined ? true : b < a;
    }
    case "limit":
      if (to === null) {
        return true;
      }
      if (from === null) {
        return false;
      }
      return typeof from === "number" && typeof to === "number" ? to > from : true;
    case "domains": {
      const a = webDomainsSchema.safeParse(from);
      const b = webDomainsSchema.safeParse(to);
      return a.success && b.success ? !webDomainsNarrow(b.data, a.data) : true;
    }
    case "list":
      return Array.isArray(from) && Array.isArray(to)
        ? to.some((item) => !from.includes(item))
        : true;
    case "egress": {
      const a = egressPolicySchema.safeParse(from);
      const b = egressPolicySchema.safeParse(to);
      return a.success && b.success ? egressLoosenedAudiences(a.data, b.data).length > 0 : true;
    }
    case "nativeClients": {
      const a = nativeClientsPolicySchema.safeParse(from);
      const b = nativeClientsPolicySchema.safeParse(to);
      return a.success && b.success ? a.data.mode !== "any" && b.data.mode === "any" : true;
    }
    case "providers": {
      const a = modelProvidersSchema.safeParse(from);
      const b = modelProvidersSchema.safeParse(to);
      return a.success && b.success ? modelProvidersWiden(a.data, b.data) : true;
    }
    case "labProvider":
      return labProviderWidens(from, to);
  }
}

type LabChoices = Readonly<Record<string, { provider: string; choices: readonly string[] }>>;

function labProviderWidens(from: unknown, to: unknown): boolean {
  if (!isLabChoices(from) || !isLabChoices(to)) {
    return true;
  }
  const choicesOf = (value: LabChoices, lab: string): readonly string[] =>
    value[lab]?.choices ?? ["esi"];
  const isDefault = (value: LabChoices, lab: string) => {
    const choice = value[lab];
    return !choice || (choice.provider === "esi" && choice.choices.join() === "esi");
  };
  const widens = Object.entries(to).some(([lab, choice]) => {
    const before = choicesOf(from, lab);
    return !before.includes(choice.provider) || choice.choices.some((c) => !before.includes(c));
  });
  // A lab left out reads as ESI only: wider when the previous value restricted it.
  return widens || Object.keys(from).some((lab) => !(lab in to) && !isDefault(from, lab));
}

function isLabChoices(value: unknown): value is LabChoices {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.values(value).every(
      (c) =>
        typeof c === "object" &&
        c !== null &&
        typeof (c as { provider?: unknown }).provider === "string" &&
        Array.isArray((c as { choices?: unknown }).choices),
    )
  );
}

/** `tools.egress`'s audiences (readers floors), in the order the rows show them. */
export const EGRESS_AUDIENCES = [
  "project",
  "entity",
  "named",
] as const satisfies readonly (keyof EgressPolicy)[];

/** An egress action's strictness: Allowed, Asks first, Blocked (the registry's order). */
export const EGRESS_RANK: Readonly<Record<EgressPolicy[keyof EgressPolicy], number>> = {
  allow: 0,
  confirm: 1,
  block: 2,
};

/** The audiences `to` moves toward Allowed from `from` (settings-typed-controls D10). */
export function egressLoosenedAudiences(
  from: EgressPolicy,
  to: EgressPolicy,
): (keyof EgressPolicy)[] {
  return EGRESS_AUDIENCES.filter((a) => EGRESS_RANK[to[a]] < EGRESS_RANK[from[a]]);
}
